/* Spend & Gift Offer - cart progress bar + free gift picker.
   Vanilla JS, no jQuery dependency.

   HOW THIS WORKS (v6):
   Earlier versions tried to fetch pre-rendered widget HTML from
   Shopify's Section Rendering API (/cart?section_id=main). Testing
   confirmed that endpoint returns a 404 for this theme's JSON-template
   cart section on this store, regardless of theme or preview state -
   so it can't be relied on.

   Instead: sections/spend-gift-global.liquid renders ONE static,
   hidden copy of the widget on every page load (via
   {% section 'spend-gift-global' %} in layout/theme.liquid), using its
   own small settings block. That copy already carries everything
   needed as data-* attributes (spend threshold, message templates,
   button text) and already-rendered gift product buttons/images/
   prices - none of that changes as the cart changes, so it only needs
   to be rendered once per page load, not re-fetched.

   This script:
     1. Clones that hidden widget into the mini-cart dropdown and the
        add-to-cart popup's mount points (and finds the real /cart
        page's own copy, rendered separately by the Main Cart section).
     2. Whenever the cart might have changed, fetches /cart.js (a
        normal, reliable Shopify AJAX endpoint - not a section render)
        and updates each widget's progress bar, message and qualified
        state purely in JS, using the data-* attributes as config.
     3. Keeps the header mini-cart drawer's scrollable item list sized
        exactly to fit between its header and its checkout footer (see
        layoutCartDrawer below), so the checkout button is always on
        screen no matter how tall the item list/gift picker gets.
*/
(function () {
  function getCartJson() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function findGiftLineIndex(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      var props = cart.items[i].properties;
      if (props && props['_Free Gift'] === 'Yes') {
        return i + 1; // /cart/change.js line numbers are 1-based
      }
    }
    return -1;
  }

  function removeGiftLine(index) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: index, quantity: 0 })
    });
  }

  // NOTE on duplicate cart lines: something outside this widget (most
  // likely a pre-order/backorder app reacting to cart changes - confirmed
  // NOT caused by this file: it reproduces with plain /cart/add.js and
  // /cart/change.js calls and no gift interaction at all) can silently
  // split one product's cart line into two separate lines for the exact
  // same variant. An earlier version of this file tried to auto-merge
  // those duplicate lines back together on every refresh
  // (consolidateDuplicateLines, since removed). Testing showed that
  // backfires: each merge attempt is itself a /cart/change.js call, which
  // the same server-side mechanism reacts to by splitting the line again,
  // so the "fix" and the app can trade blows indefinitely - repeatedly
  // rewriting the same lines back and forth - without ever converging,
  // instead of the rare, self-resolving duplicate it was meant to clean
  // up. That is strictly worse than leaving any already-split lines alone.
  // watchQuantityInputsForCorrectLineUpdate() below is the real fix: it
  // always targets the exact line the customer is editing by its own
  // unique key, so it gives the right result whether or not duplicate
  // lines exist, without this widget ever issuing its own "corrective"
  // cart mutations. The underlying duplicate-line behavior itself still
  // needs to be raised with the pre-order/backorder app's support (see
  // the bug report) - it isn't something a theme file can fix safely.

  // Returns a Promise so callers (wireButtons) can react to success/failure
  // themselves instead of this function owning the button's visual state.
  function addGift(variantId) {
    return getCartJson().then(function (cart) {
      var existingIndex = findGiftLineIndex(cart);
      var chain = existingIndex > -1 ? removeGiftLine(existingIndex) : Promise.resolve();

      return chain.then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { '_Free Gift': 'Yes' }
          })
        });
      }).then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) { throw err; });
        }
        return r.json();
      }).then(function () {
        // Don't just reload the current page: when this is called from the
        // mini-cart dropdown or the add-to-cart ("Quick Cart") popup, the
        // current page is whatever product/collection page the customer was
        // browsing - reloading it closes the popup/drawer and leaves the
        // customer back on that page with no visible confirmation, even
        // though the gift WAS added (this is what "it's not updating" /
        // "only works from the cart page" was: the add always succeeded,
        // but reloading the product page gave no visible sign of it).
        //
        // The theme's own in-place popup/drawer refresh (ella.*) lives
        // inside halo.js's private closure and isn't callable from here, so
        // rather than fragile re-implementation, send the customer straight
        // to the real /cart page, where the item list, totals and this
        // widget are all rendered fresh server-side and guaranteed correct.
        // Preserve the current query string (e.g. ?preview_theme_id=... while
        // QAing on the draft theme) so the destination stays on the same
        // theme/context.
        window.location.href = '/cart' + window.location.search;
      });
    });
  }

  // Wires up every "ADD MY FREE GIFT" button in one widget instance
  // (root = the cart-page modal, or one popup/drawer clone).
  //
  // Two problems reported together turned out to share one root cause: the
  // add-gift call chain (read cart -> maybe remove old gift line -> add new
  // line) takes under a second normally, but nothing on screen changed while
  // it ran, so a customer who didn't see an instant reaction would click a
  // second, different gift option before the first click's request had even
  // finished. Because each click started its own independent
  // read-cart-then-write chain, two clicks in flight at once could both read
  // "no gift in cart yet" before either had written its own gift line,
  // so BOTH ended up added - "allows multiple selection" - and, since the
  // page only navigates to /cart once the LAST click's chain resolves, that
  // also produced the long, unpredictable ("40+ seconds") wait: every extra
  // click added its own full read/write/navigate sequence competing with the
  // others, rather than one clean sub-second request.
  //
  // Fix: the instant a gift button is clicked, synchronously (before any
  // network call starts) disable every gift button in this widget and mark
  // the clicked one as loading. That closes the window completely - a
  // second click, on the same button or a different one, is a no-op for as
  // long as the first request is in flight - so only one add-gift chain can
  // ever run at a time per widget, and the customer gets an immediate
  // "Adding..." reaction instead of a dead-looking button.
  function wireButtons(root) {
    if (!root) return;
    var buttons = root.querySelectorAll ? root.querySelectorAll('[data-spend-gift-add]') : [];
    buttons.forEach(function (btn) {
      if (btn.dataset.sgWired) return;
      btn.dataset.sgWired = '1';
      btn.dataset.sgOriginalText = btn.textContent;
      btn.addEventListener('click', function (e) {
        e.preventDefault();

        // Already processing a click (this button or a sibling gift option
        // in the same widget) - ignore extra clicks until it settles.
        if (root.dataset.sgAdding === '1') return;
        root.dataset.sgAdding = '1';

        buttons.forEach(function (b) {
          b.disabled = true;
          b.classList.add('is-loading');
        });
        btn.classList.add('is-added');
        btn.textContent = 'Adding...';

        addGift(btn.getAttribute('data-variant-id')).catch(function () {
          // Failed - unlock everything so the customer can try again,
          // instead of leaving every gift button permanently disabled.
          root.dataset.sgAdding = '0';
          buttons.forEach(function (b) {
            b.disabled = false;
            b.classList.remove('is-loading');
          });
          btn.classList.remove('is-added');
          btn.textContent = btn.dataset.sgOriginalText;
          window.alert('Sorry, we could not add your free gift right now. Please try again.');
        });
        // No success handler needed: addGift() navigates the page to /cart
        // on success, which naturally supersedes any loading state here.
      });
    });
  }

  // The merchant enters the spend threshold once, in GBP (the shop's base
  // currency) - see the comment in snippets/spend-gift-offer.liquid for why
  // that number alone can't be compared against a cart total in another
  // currency. Shopify Markets already exposes the exact live rate it uses
  // to convert every product/cart price for the visitor's market as
  // `Shopify.currency.rate` (e.g. {active:"PKR", rate:"381.09"} - confirmed
  // live on this store, and it moves with the real exchange rate, unlike a
  // hand-typed number). Multiplying the GBP threshold by this rate gives the
  // equivalent threshold in whatever currency /cart.js is already reporting
  // the cart total in, so the offer now works correctly in every currency
  // Shopify Markets supports rather than only the couple of currencies
  // someone manually priced in ahead of time.
  function getThresholdCents(widgetEl) {
    var baseCents = parseInt(widgetEl.getAttribute('data-threshold-cents'), 10) || 0;
    var rate = 1;
    try {
      var rawRate = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate;
      var parsedRate = parseFloat(rawRate);
      if (parsedRate > 0) rate = parsedRate;
    } catch (e) {
      // window.Shopify.currency not available for some reason - fall back
      // to treating the cart as already being in the base (GBP) currency,
      // which is the previous behavior and still correct for GBP visitors.
    }
    return Math.round(baseCents * rate);
  }

  function formatMoney(cents, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  // --- Header mini-cart drawer layout -----------------------------------
  // #dropdown-cart has a header (.cart-title), a scrollable item list
  // (.has-items) and a checkout footer (.spend-gift-drawer-footer, added
  // in snippets/wrapper-top-cart.liquid). CSS gives .has-items a rough
  // max-height fallback, but the real available space varies by
  // breakpoint (desktop: a dropdown panel that opens below the
  // announcement bar + nav, with no visible .cart-title; mobile: a
  // full-screen takeover starting at the very top, with .cart-title
  // visible and extra top padding before it) and by content (whether
  // the free-gift tag or terms checkbox is shown changes the footer's
  // height).
  //
  // Rather than adding up the drawer's top offset + title height +
  // padding (fragile - it has to match every gap the theme's own CSS
  // puts above .has-items, on every breakpoint), this reads
  // .has-items' own on-screen top position directly via
  // getBoundingClientRect(). That single number already reflects
  // everything above it - the drawer's position, any top padding, the
  // header bar when it's shown - so the only other real unknown is the
  // footer's own height. This is correct regardless of header height,
  // sticky/fixed positioning, or breakpoint, and keeps the checkout
  // footer on screen at all times instead of guessing fixed pixel
  // values that broke on mobile (full-screen drawer with a visible
  // title bar) the first time this was tried.
  function layoutCartDrawer() {
    var drawer = document.getElementById('dropdown-cart');
    if (!drawer) return;
    var hasItems = drawer.querySelector('.has-items');
    if (!hasItems) return;
    var hasItemsRect = hasItems.getBoundingClientRect();
    // Skip while the drawer is closed - a display:none element (or one
    // inside a display:none ancestor) reports an all-zero rect, and
    // using that would wrongly assume .has-items starts at the very
    // top of the screen with nothing above it (see watchForPopupOpen /
    // scheduleLayoutCartDrawer, which re-run this once it opens).
    if (hasItemsRect.width === 0 && hasItemsRect.height === 0 && hasItemsRect.top === 0) return;
    var footer = drawer.querySelector('[data-drawer-footer]');
    var footerH = footer ? footer.offsetHeight : 0;
    var SAFETY_MARGIN = 8;
    var available = window.innerHeight - hasItemsRect.top - footerH - SAFETY_MARGIN;
    if (available > 100) {
      hasItems.style.maxHeight = available + 'px';
    }
  }

  var layoutDrawerTimer = null;
  function scheduleLayoutCartDrawer() {
    if (layoutDrawerTimer) clearTimeout(layoutDrawerTimer);
    layoutDrawerTimer = setTimeout(layoutCartDrawer, 30);
  }

  // --- Cart-page "Select your free gift!" modal -----------------------
  // Only the real /cart page's own widget (rendered by
  // snippets/spend-gift-offer.liquid) is wrapped in a
  // [data-spend-gift-wrapper] with a sibling [data-spend-gift-modal].
  // The mini-cart dropdown and add-to-cart popup clones (rendered from
  // spend-gift-offer-global.liquid) have neither, so all of this is a
  // no-op for them - findModalFor() just returns null.
  var SG_MODAL_SHOWN_KEY = 'sgGiftModalAutoShown';

  function hasAutoShownModal() {
    try {
      return sessionStorage.getItem(SG_MODAL_SHOWN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markModalAutoShown() {
    try {
      sessionStorage.setItem(SG_MODAL_SHOWN_KEY, '1');
    } catch (e) {}
  }

  function findModalFor(widgetEl) {
    var wrapper = widgetEl.closest('[data-spend-gift-wrapper]');
    return wrapper ? wrapper.querySelector('[data-spend-gift-modal]') : null;
  }

  function openGiftModal(modalEl) {
    if (!modalEl) return;
    modalEl.style.display = 'flex';
    modalEl.setAttribute('aria-hidden', 'false');
    document.body.classList.add('spend-gift-modal-open');
  }

  function closeGiftModal(modalEl) {
    if (!modalEl) return;
    modalEl.style.display = 'none';
    modalEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('spend-gift-modal-open');
  }

  function wireModal(modalEl) {
    if (!modalEl || modalEl.dataset.sgModalWired) return;
    modalEl.dataset.sgModalWired = '1';
    var closers = modalEl.querySelectorAll('[data-spend-gift-modal-close]');
    closers.forEach(function (el) {
      el.addEventListener('click', function () { closeGiftModal(modalEl); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modalEl.style.display !== 'none') closeGiftModal(modalEl);
    });
  }

  function wireReopenButton(widgetEl, modalEl) {
    var btn = widgetEl.querySelector('[data-spend-gift-reopen]');
    if (!btn || btn.dataset.sgWired) return;
    btn.dataset.sgWired = '1';
    btn.addEventListener('click', function () { openGiftModal(modalEl); });
  }

  // Recompute one widget instance's dynamic bits (progress bar width,
  // message text, qualified state, gift list visibility) purely from
  // the live cart, using the config baked into its own data-*
  // attributes at render time. Works identically for the real /cart
  // page's own copy and for any clone sitting in a mount point, since
  // they all come from the same 'spend-gift-offer' markup/attributes.
  function applyCartState(widgetEl, cart) {
    var thresholdCents = getThresholdCents(widgetEl);
    var headingTemplate = widgetEl.getAttribute('data-heading-template') || '';
    var qualifiedHeading = widgetEl.getAttribute('data-qualified-heading') || '';
    var total = cart.total_price;
    var qualified = thresholdCents > 0 && total >= thresholdCents;
    var remaining = thresholdCents - total;
    if (remaining < 0) remaining = 0;
    var percent = thresholdCents > 0 ? Math.min(100, (total * 100) / thresholdCents) : 100;

    widgetEl.classList.toggle('is-qualified', qualified);

    var message = widgetEl.querySelector('[data-spend-gift-message]');
    if (message) {
      if (qualified) {
        message.textContent = qualifiedHeading;
      } else {
        var remainingMoney = formatMoney(remaining, cart.currency);
        message.textContent = headingTemplate.replace('[amount]', remainingMoney);
      }
    }

    var barFill = widgetEl.querySelector('[data-spend-gift-bar-fill]');
    if (barFill) barFill.style.width = percent + '%';
    var bar = widgetEl.querySelector('.spend-gift-offer__bar');
    if (bar) bar.setAttribute('aria-valuenow', Math.round(percent));

    var products = widgetEl.querySelector('[data-spend-gift-products]');
    if (products) products.style.display = qualified ? '' : 'none';

    var reopenBtn = widgetEl.querySelector('[data-spend-gift-reopen]');
    if (reopenBtn) reopenBtn.style.display = qualified ? '' : 'none';
  }

  function ensureMountsPopulated() {
    var source = document.querySelector('#spend-gift-global-source [data-spend-gift-offer]');
    if (!source) return;
    var mounts = document.querySelectorAll('[data-spend-gift-mount]');
    mounts.forEach(function (mount) {
      if (mount.querySelector('[data-spend-gift-offer]')) return;
      var clone = source.cloneNode(true);
      clone.removeAttribute('style');
      mount.innerHTML = '';
      mount.appendChild(clone);
      wireButtons(clone);
    });
  }

  function refreshAll() {
    var source = document.querySelector('#spend-gift-global-source [data-spend-gift-offer]');
    var mountCount = document.querySelectorAll('[data-spend-gift-mount]').length;
    var staticRoot = document.querySelector('.cart-template [data-spend-gift-offer]');

    // Feature is off (or this section/settings aren't present on this
    // page for some reason) - nothing to do.
    if (!source && !staticRoot) return;
    if (!mountCount && !staticRoot) return;

    ensureMountsPopulated();

    getCartJson().then(function (cart) {
      document.querySelectorAll('[data-spend-gift-offer]').forEach(function (widgetEl) {
        // Skip the hidden master copy itself - only visible instances matter.
        if (widgetEl.closest('#spend-gift-global-source')) return;
        applyCartState(widgetEl, cart);

        // Cart-page "Select your free gift!" modal: auto-open the first
        // time this session the cart qualifies, then leave it to the
        // "Change your gift" button from then on (see wireReopenButton).
        var modalEl = findModalFor(widgetEl);
        if (modalEl) {
          wireModal(modalEl);
          wireButtons(modalEl);
          wireReopenButton(widgetEl, modalEl);

          var thresholdCents = getThresholdCents(widgetEl);
          var qualifiedNow = thresholdCents > 0 && cart.total_price >= thresholdCents;
          if (qualifiedNow && !hasAutoShownModal()) {
            markModalAutoShown();
            openGiftModal(modalEl);
          }
        }
      });

      // Safety net: if the cart has dropped back below the threshold,
      // remove any previously-added free gift line so the offer can't
      // be exploited and the cart doesn't show a stale free item.
      var reference = source || staticRoot;
      var thresholdCents = getThresholdCents(reference);
      var stillQualified = thresholdCents > 0 && cart.total_price >= thresholdCents;
      if (!stillQualified) {
        var idx = findGiftLineIndex(cart);
        if (idx > -1) {
          removeGiftLine(idx).then(function () { location.reload(); });
        }
      }

      scheduleLayoutCartDrawer();
    }).catch(function (err) {
      console.error('[SpendGiftOffer] Failed to refresh the spend & gift widget:', err);
    });
  }

  // The theme opens the mini-cart dropdown and the add-to-cart popup via
  // its own JS after an ajax add-to-cart call, without a page reload.
  // Watch those containers for visibility/class changes and refresh our
  // mounted widget whenever they open, so it reflects the item that was
  // just added instead of being stuck on stale data.
  function watchForPopupOpen(selector) {
    var el = document.querySelector(selector);
    if (!el || !window.MutationObserver) return;
    var observer = new MutationObserver(function () {
      refreshAll();
      scheduleLayoutCartDrawer();
    });
    observer.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  // Fallback: shortly after any "Add to cart" submit, refresh too, in case
  // the popup's visibility change happens in a way the observer above
  // doesn't catch (belt-and-braces, cheap no-op if nothing changed).
  function watchAddToCartClicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-btn-addToCart]') : null;
      if (!btn) return;
      setTimeout(refreshAll, 700);
    });
  }

  // Also refresh whenever a customer manually opens the mini-cart via the
  // header cart icon (not just right after adding something), so it never
  // shows stale progress if items changed some other way.
  function watchCartIconOpen() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-cart-toggle]') : null;
      if (!btn) return;
      setTimeout(refreshAll, 300);
      setTimeout(scheduleLayoutCartDrawer, 300);
    });
  }

  // Refresh whenever a quantity is changed in-place: the /cart page's own
  // "updates[]" inputs (snippets/cart-template.liquid,
  // class="update-cart--template") and the +/- steppers next to them
  // (which just set the input's value and fire a "change" event on it -
  // see halo.js changeQuantityAddToCart), plus the header mini-cart
  // dropdown's own quantity input (snippets/wrapper-top-cart.liquid,
  // class="item-quantity"). The theme updates the cart total via its own
  // AJAX call (ella.doAjaxUpdateCart / doAjaxUpdatePopupCart) on these
  // "change" events without a page reload, so without this the spend &
  // gift progress bar/message is left showing stale numbers until the
  // customer manually refreshes.
  //
  // This is a capture-phase listener on document rather than the usual
  // bubble-phase delegation used elsewhere in this file: the theme's own
  // handler for the mini-cart's quantity input calls
  // e.stopPropagation(), which would stop a bubble-phase document
  // listener from ever seeing the event. A capture-phase listener on
  // document always runs on the way down, before that handler gets a
  // chance to stop anything, so it isn't affected either way - and it
  // still works fine for the /cart page's own input, whose handler
  // doesn't stop propagation at all.
  function watchQuantityChanges() {
    document.addEventListener('change', function (e) {
      var input = e.target.closest ? e.target.closest('.update-cart--template, .item-quantity') : null;
      if (!input) return;
      setTimeout(refreshAll, 700);
    }, true);
  }

  // --- Correct-line quantity updates (fixes the theme's variant-ID mixup) -
  // halo.js's own quantity-change handlers identify which cart line to
  // update using the product's VARIANT ID (data-item-id / data-product-id),
  // not the specific line - see the note above removeGiftLine for why that
  // goes wrong once two lines share a variant (which happens here for at
  // least one pre-order product, caused by something outside this theme,
  // not by this widget).
  //
  // This runs in the capture phase - same technique as watchQuantityChanges
  // above - so it fires before halo.js's own handler (bound directly on the
  // input, so it only ever runs once the event reaches the input itself,
  // which is after the capture phase has already passed through). Calling
  // stopImmediatePropagation() here stops that handler from running at all,
  // and this performs the update itself against the exact cart line - its
  // own unique key (data-line-key, added in cart-template.liquid /
  // wrapper-top-cart.liquid), never the ambiguous variant ID - so it can
  // never land on the wrong line even when duplicates exist.
  //
  // A full reload afterward guarantees every other on-screen total,
  // discount banner and mini-cart row is exactly correct, rather than
  // hand-rebuilding halo.js's own private refresh logic (the same
  // reasoning already used for the free-gift add above). If the input
  // predates this fix (no data-line-key rendered yet, e.g. a cached page),
  // this steps aside and lets the theme handle it exactly as before.
  function watchQuantityInputsForCorrectLineUpdate() {
    document.addEventListener('change', function (e) {
      var input = e.target.closest ? e.target.closest('.update-cart--template, .item-quantity') : null;
      if (!input) return;
      var lineKey = input.dataset.lineKey;
      if (!lineKey) return;

      e.stopImmediatePropagation();

      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;

      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      }).then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) { throw err; });
        }
        return r.json();
      }).then(function () {
        location.reload();
      }).catch(function (err) {
        console.error('[SpendGiftOffer] Failed to update cart quantity:', err);
        window.alert('Sorry, we could not update the quantity right now. Please try again.');
        location.reload();
      });
    }, true);
  }

  // Same reasoning as watchQuantityChanges above, but for removing a
  // line item entirely: the /cart page's trash icon
  // (.cart-form .remove / .remove-mobile) and the mini-cart dropdown's
  // (.btn-remove) both call e.stopPropagation() in their theme click
  // handlers (halo.js), so this also has to run in the capture phase to
  // reliably see the click.
  function watchRemoveClicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.cart-form .remove, .btn-remove') : null;
      if (!btn) return;
      setTimeout(refreshAll, 700);
    }, true);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var source = document.querySelector('#spend-gift-global-source [data-spend-gift-offer]');
    var mountCount = document.querySelectorAll('[data-spend-gift-mount]').length;
    var staticRoot = document.querySelector('[data-spend-gift-offer]');
    console.log('[SpendGiftOffer] script loaded. global source found:', !!source, '| mount points found:', mountCount, '| any widget on this page:', !!staticRoot);
    if (!source && !staticRoot) return;

    document.querySelectorAll('[data-spend-gift-offer]').forEach(function (el) {
      if (el.closest('#spend-gift-global-source')) return;
      wireButtons(el);
    });

    refreshAll();
    watchForPopupOpen('[data-ajax-cart-success]');
    watchForPopupOpen('#dropdown-cart');
    watchAddToCartClicks();
    watchCartIconOpen();
    watchQuantityInputsForCorrectLineUpdate();
    watchQuantityChanges();
    watchRemoveClicks();

    layoutCartDrawer();
    window.addEventListener('resize', scheduleLayoutCartDrawer);
    window.addEventListener('orientationchange', scheduleLayoutCartDrawer);
  });
})();
